import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import AdvSelectInput from "@builder/components/app/Settings/StoreData/AdvSelectInput";
import { useDebounce } from "@builder/components/app/Settings/StoreData/useDebounce";
import { useGetCustomersQuery } from "@builder/store/slices/dataApiSlices/customersApiSlice";
import { setStoreData } from "@builder/store/slices/storeDataSlice";
import { Customer } from "@builder/types/Customer.type";

interface CustomerProps {
    companyId: string;
    organizationId?: string;
    isOrg: boolean | null;
}

const Customers = ({ companyId, organizationId, isOrg }: CustomerProps) => {
    const dispatch = useAppDispatch();
    const customer = useAppSelector(state => state.storeData.data?.customer);
    const [options, setOptions] = useState<Customer[]>([]);
    const [search, setSearch] = useState<string>("");
    const [skip, setSkip] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");

    const { data: customers, isFetching } = useGetCustomersQuery({
        companyId,
        search: debouncedSearch,
        offset: skip,
        limit: 25,
        ...(isOrg && { organizationId })
    });

    const debouncedSearchCallback = useDebounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
    }, 300);

    useEffect(() => {
        if (customers) {
            setLoading(false);

            if (skip === 0) {
                setOptions(customers);
            } else {
                setOptions(prev => {
                    const newCustomers = customers.filter(customer => !prev.some(existing => existing._id === customer._id));
                    return [...prev, ...newCustomers];
                });
            }
            setHasMore(customers.length === 25);
        }
    }, [customers, skip]);

    useEffect(() => {
        debouncedSearchCallback(search);
    }, [search, debouncedSearchCallback]);

    useEffect(() => {
        setSkip(0);
        setDebouncedSearch("");
    }, [companyId]);

    useEffect(() => {
        setSkip(0);
    }, [debouncedSearch]);

    const handleScroll = () => {
        if (hasMore && !loading && !isFetching) {
            setLoading(true);
            setSkip(prevSkip => prevSkip + 25);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
    };

    const handleOptionChange = (value: Customer) => {
        dispatch(setStoreData({ customer: value }));
    };
    return (
        <AdvSelectInput
            value={customer}
            options={options}
            inputValue={search}
            label="Customers"
            placeholder={options.length === 0 ? "No customers available" : ""}
            disabled={options.length === 0}
            hasMore={hasMore}
            findLabel={option => option?.email || ""}
            clickCallback={handleOptionChange}
            scrollCallback={handleScroll}
            searchCallback={handleSearchChange}
        />
    );
};

export default Customers;
